import { Close } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import React, { memo, useEffect, useState } from "react";
import { getUnitApi } from "../../../services/unit.service";
import { useGetApi } from "../../../hooks/useGetApi";
import { getUserApi } from "../../../services/user.service";
import { toast } from "react-toastify";
import { createConsultationUnitTransfersApi } from "../../../services/consultation.service";

const InitiateUnitTransferModal = ({ open, onClose, selectedRow, refetch }) => {
  const initialState = {
    visitId: selectedRow?.occupancyDetails?.visitId || "",
    requestedUnit: null,
    doctor: null,
    transferReason: "",
  };

  const [formData, setFormData] = useState(initialState);
  const [isLoading, setIsLoading] = useState(false);

  const { dataList: unitList } = useGetApi({
    apiFunction: getUnitApi,
    params: { limit: Number.MAX_SAFE_INTEGER },
    dataKey: "units",
  });

  const filteredUnitList = unitList?.filter(
    (unit) => selectedRow?.occupancyDetails?.unit !== unit?._id
  );

  const { dataList: userList } = useGetApi({
    apiFunction: getUserApi,
    params: { limit: Number.MAX_SAFE_INTEGER },
    dataKey: "users",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevValue) => {
      return {
        ...prevValue,
        [name]: value,
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsLoading(true);
    const response = await createConsultationUnitTransfersApi({
      ...formData,
      requestedUnit: formData?.requestedUnit?._id,
      doctor: formData?.doctor?._id,
    });
    setIsLoading(false);

    if (response?.code === 200) {
      toast.success(
        response?.message || `Unit Transfer initiated successfully`
      );
      setFormData(initialState);
      onClose();
      refetch();
    } else {
      toast.error(response?.message || "Some error occurred.");
    }
  };

  useEffect(() => {
    setFormData((prevValue) => ({
      ...prevValue,
      visitId: selectedRow?.occupancyDetails?.visitId || "",
    }));
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            maxWidth: "unset",
            width: { xs: "95vw", lg: "1100px" },
            bgcolor: "#E5F4FF",
            borderRadius: "20px",
          },
        },
      }}
    >
      <DialogContent>
        <Box component="form" onSubmit={handleSubmit}>
          {/* dialog title */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: "20px",
            }}
          >
            <Typography variant="h6" sx={{ color: "#5D6A82" }}>
              Initiate Unit Transfer
            </Typography>
            <Close
              onClick={onClose}
              sx={{ cursor: "pointer", color: "#5D6A82" }}
            />
          </Box>

          <Grid
            container
            spacing={3}
            sx={{
              mb: "40px",
            }}
          >
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                options={filteredUnitList || []}
                getOptionLabel={(option) => option?.unitName || ""}
                renderInput={(params) => (
                  <TextField {...params} label="Unit Name" required fullWidth />
                )}
                value={formData?.requestedUnit || null}
                onChange={(_, newValue) =>
                  handleChange({
                    target: { name: "requestedUnit", value: newValue },
                  })
                }
                sx={{ background: "white" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                options={userList || []}
                getOptionLabel={(option) =>
                  `${option?.firstName || ""} ${option?.lastName || ""} (${
                    option?.userId || ""
                  })`
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Doctor Name"
                    fullWidth
                    required
                  />
                )}
                value={formData?.doctor || null}
                onChange={(_, newValue) =>
                  handleChange({
                    target: { name: "doctor", value: newValue },
                  })
                }
                sx={{ background: "white" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Reason"
                name="transferReason"
                value={formData?.transferReason || ""}
                onChange={handleChange}
                required
                fullWidth
                sx={{ bgcolor: "white" }}
              />
            </Grid>
          </Grid>

          {/* submit button */}
          <Box sx={{ my: "20px", textAlign: "center" }}>
            <Button
              type="submit"
              variant="contained"
              sx={{ width: "150px", textTransform: "none" }}
              disabled={isLoading}
            >
              {isLoading ? <CircularProgress size={24} /> : `Submit`}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default memo(InitiateUnitTransferModal);
